// Server functions for the wearable integration (Junction OAuth + readback).
import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { requireAdminAuth } from "@/integrations/supabase/admin-middleware";
// NOTE: `client.server` is dynamically imported inside handler bodies (and
// module-local helpers those handlers call) so the service-role module is
// never pulled into the client bundle. Matches the pattern in
// labs.functions.ts and admin.functions.ts.
import { assertActiveAccess } from "@/integrations/supabase/access-check.server";
import { isAllowedAppOrigin, APP_APEX_ORIGIN } from "@/lib/app-origins";
import {
  WEARABLE_PROVIDERS,
  type WearableProvider,
} from "@/lib/wearables-shared";
import {
  createJunctionDemoConnection,
  createJunctionLinkToken,
  disconnectJunctionProvider,
  ensureJunctionUser,
  fetchJunctionProviderStates,
  isJunctionSandbox,
} from "@/lib/junction.server";

// Map a Junction provider status to our wearable_connections.status enum
// (pending | connected | disconnected | error). Junction is the source of
// truth — if it tells us a provider deregistered, we record that as
// disconnected (user revoked at the provider); other Junction errors are
// surfaced as our generic "error". Returns null when we shouldn't change
// the row (unknown / unmapped Junction status).
function mapJunctionStatus(
  j: { status: string | null; errorType: string | null },
): "connected" | "disconnected" | "error" | null {
  const s = (j.status || "").toLowerCase();
  if (s === "connected") return "connected";
  if (s === "error") {
    if (j.errorType === "deregistered_per_provider") return "disconnected";
    return "error";
  }
  if (s === "disconnected" || s === "deleted" || s === "deregistered") return "disconnected";
  return null;
}

// Reconcile one stored connection against Junction's truth. Safe to call
// from user-facing paths: failures are swallowed (logged) so the screen
// never crashes. Skips rows without junction_user_id (manually-seeded test
// rows have no Junction counterpart). Throttled — only re-checks rows
// whose updated_at is older than RECONCILE_TTL_MS.
const RECONCILE_TTL_MS = 30 * 60 * 1000; // 30 minutes

async function reconcileWearableConnection(
  row: {
    user_id: string;
    provider: string;
    junction_user_id: string | null;
    status: string;
    updated_at: string | null;
  },
  opts: { force?: boolean } = {},
): Promise<void> {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  if (!row.junction_user_id) return;
  if (!opts.force && row.updated_at) {
    const age = Date.now() - new Date(row.updated_at).getTime();
    if (Number.isFinite(age) && age < RECONCILE_TTL_MS) return;
  }
  try {
    const states = await fetchJunctionProviderStates(row.junction_user_id);
    const match = states.find((p) => p.slug === row.provider);
    if (!match) return;
    const mapped = mapJunctionStatus(match);
    if (!mapped) return;
    if (mapped === row.status) {
      // Touch updated_at so we honor the throttle next call.
      await supabaseAdmin
        .from("wearable_connections")
        .update({ updated_at: new Date().toISOString() })
        .eq("user_id", row.user_id)
        .eq("provider", row.provider);
      return;
    }
    const patch: {
      status: "connected" | "disconnected" | "error";
      updated_at: string;
      last_error?: string | null;
      disconnected_at?: string | null;
    } = {
      status: mapped,
      updated_at: new Date().toISOString(),
    };
    if (mapped === "disconnected" || mapped === "error") {
      patch.last_error = match.errorMessage
        ? `${match.errorType || "error"}: ${match.errorMessage}`
        : match.errorType || "Junction reports provider disconnected";
      patch.disconnected_at = match.erroredAt || new Date().toISOString();
    } else if (mapped === "connected") {
      patch.last_error = null;
      patch.disconnected_at = null;
    }
    await supabaseAdmin
      .from("wearable_connections")
      .update(patch)
      .eq("user_id", row.user_id)
      .eq("provider", row.provider);
    console.log(
      "[junction] reconciled connection",
      JSON.stringify({
        user_id: row.user_id,
        provider: row.provider,
        was: row.status,
        now: mapped,
        errorType: match.errorType,
      }),
    );
  } catch (e: any) {
    console.warn("[junction] reconcile failed", row.provider, e?.message);
  }
}

function isProvider(v: unknown): v is WearableProvider {
  return typeof v === "string" && (WEARABLE_PROVIDERS as readonly string[]).includes(v);
}

async function getOrResolveJunctionUserId(userId: string): Promise<string> {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { data: existing, error } = await supabaseAdmin
    .from("wearable_connections")
    .select("junction_user_id")
    .eq("user_id", userId)
    .not("junction_user_id", "is", null)
    .limit(1)
    .maybeSingle();
  if (error) throw new Error(error.message);

  if (existing?.junction_user_id) {
    console.log(
      "[junction] user path: used stored id",
      JSON.stringify({ appUserId: userId, userId: existing.junction_user_id }),
    );
    return existing.junction_user_id;
  }

  return ensureJunctionUser(userId);
}


export const listWearableConnections = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { supabase, userId } = context;
    try {
      await assertActiveAccess(userId);
    } catch {
      // Non-premium users: return empty list instead of throwing so the
      // dashboard renders without a blank screen.
      return { connections: [] as any[], premiumRequired: true };
    }


    // Reconcile any real Junction-backed rows against Junction's truth
    // (throttled to RECONCILE_TTL_MS inside the helper). Skipped rows
    // without junction_user_id. Failures are swallowed so the screen
    // still renders even if Junction is down.
    const { data: reconcileRows } = await supabaseAdmin
      .from("wearable_connections")
      .select("user_id, provider, junction_user_id, status, updated_at")
      .eq("user_id", userId)
      .not("junction_user_id", "is", null);
    if (reconcileRows?.length) {
      await Promise.all(reconcileRows.map((r) => reconcileWearableConnection(r as any)));
    }

    const { data, error } = await supabase
      .from("wearable_connections")
      .select("provider, status, connected_at, last_synced_at, last_error")
      .eq("user_id", userId);
    if (error) throw new Error(error.message);
    return { connections: data ?? [], premiumRequired: false };
  });

export const startWearableConnect = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { provider: string; redirectUrl: string }) => {
    if (!isProvider(input.provider)) throw new Error("Invalid provider");
    if (!input.redirectUrl || !/^https?:\/\//.test(input.redirectUrl)) {
      throw new Error("redirectUrl must be a full URL");
    }
    // Validate origin against the app-owned allowlist so the wearable OAuth
    // flow can't be used as an open redirect via Junction. Fall back to the
    // production apex when the supplied origin isn't ours.
    let safeRedirect = input.redirectUrl;
    try {
      const u = new URL(input.redirectUrl);
      if (!isAllowedAppOrigin(u.origin)) {
        safeRedirect = APP_APEX_ORIGIN;
      }
    } catch {
      safeRedirect = APP_APEX_ORIGIN;
    }
    return { provider: input.provider, redirectUrl: safeRedirect };
  })
  .handler(async ({ data, context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { userId } = context;
    await assertActiveAccess(userId);

    const junctionUserId = await getOrResolveJunctionUserId(userId);

    // Upsert a pending connection row.
    const { error: upsertErr } = await supabaseAdmin

      .from("wearable_connections")
      .upsert(
        [
          {
            user_id: userId,
            provider: data.provider,
            junction_user_id: junctionUserId,
            status: "pending",
          },
        ],
        { onConflict: "user_id,provider" },
      );
    if (upsertErr) throw new Error(upsertErr.message);

    const { linkUrl } = await createJunctionLinkToken({
      junctionUserId,
      provider: data.provider,
      redirectUrl: data.redirectUrl,
    });

    return { linkUrl };
  });

export const disconnectWearable = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { provider: string }) => {
    if (!isProvider(input.provider)) throw new Error("Invalid provider");
    return { provider: input.provider };
  })
  .handler(async ({ data, context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { userId } = context;
    await assertActiveAccess(userId);

    const { data: row } = await supabaseAdmin
      .from("wearable_connections")
      .select("junction_user_id")
      .eq("user_id", userId)
      .eq("provider", data.provider)
      .maybeSingle();

    if (row?.junction_user_id) {
      try {
        await disconnectJunctionProvider({
          junctionUserId: row.junction_user_id,
          provider: data.provider,
        });
      } catch (e: any) {
        console.warn("[wearables] junction disconnect failed", e?.message);
      }
    }

    const { error } = await supabaseAdmin
      .from("wearable_connections")
      .update({
        status: "disconnected",
        disconnected_at: new Date().toISOString(),
      })
      .eq("user_id", userId)
      .eq("provider", data.provider);
    if (error) throw new Error(error.message);

    return { ok: true };
  });


// Sandbox-only: trigger Junction's synthetic data feed (POST /v2/link/connect/demo).
// Supported providers per Junction docs: fitbit, oura.
export const startWearableDemoConnect = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { provider: string }) => {
    if (input.provider !== "fitbit" && input.provider !== "oura") {
      throw new Error("Demo connection only supports fitbit or oura");
    }
    return { provider: input.provider as "fitbit" | "oura" };
  })
  .handler(async ({ data, context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    if (!isJunctionSandbox()) {
      throw new Error("Demo connect is only available against the Junction sandbox.");
    }
    const { userId } = context;
    await assertActiveAccess(userId);

    let junctionUserId = await getOrResolveJunctionUserId(userId);

    // Upsert a pending connection row so the webhook can resolve back.
    async function upsertPending(jUserId: string) {
      const { error } = await supabaseAdmin
        .from("wearable_connections")
        .upsert(
          [
            {
              user_id: userId,
              provider: data.provider,
              junction_user_id: jUserId,
              status: "pending",
            },
          ],
          { onConflict: "user_id,provider" },
        );
      if (error) throw new Error(error.message);
    }

    await upsertPending(junctionUserId!);

    await createJunctionDemoConnection({
      junctionUserId,
      provider: data.provider,
    });

    return { ok: true, junctionUserId };
  });


// Sandbox-only: seed a coherent 14-21 day test scenario directly in the DB.
// Backdates the profile, writes daily_logs + hot-flash symptom_events with a
// mild improving trend, and replaces 30 days of wearable_metrics with values
// that include skin-temp spikes overlapping hot-flash clusters.
export const seedTestScenario = createServerFn({ method: "POST" })
  .middleware([requireAdminAuth])
  .handler(async ({ context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { userId } = context;

    // 1. Backdate profile created_at to 21 days ago (admin client bypasses RLS
    //    and the protect_is_test_user trigger no-ops for non-client roles).
    const now = new Date();
    const createdAt = new Date(now.getTime() - 21 * 86400000);
    await supabaseAdmin
      .from("profiles")
      .update({ created_at: createdAt.toISOString(), is_test_user: true })
      .eq("id", userId);

    // Build local-TZ-ish YYYY-MM-DD day keys (using UTC for determinism in
    // server context — wearable_metrics already uses UTC calendar dates).
    function dayKey(daysAgo: number): string {
      const d = new Date(now.getTime() - daysAgo * 86400000);
      const y = d.getUTCFullYear();
      const m = String(d.getUTCMonth() + 1).padStart(2, "0");
      const dd = String(d.getUTCDate()).padStart(2, "0");
      return `${y}-${m}-${dd}`;
    }

    const SYMPTOM_DAYS = 21;
    const WEARABLE_DAYS = 30;

    // 2. Clear prior seeded rows for clean re-runs.
    await supabaseAdmin.from("daily_logs").delete().eq("user_id", userId).gte("log_date", dayKey(SYMPTOM_DAYS - 1));
    await supabaseAdmin.from("symptom_events").delete().eq("user_id", userId).gte("occurred_at", new Date(now.getTime() - SYMPTOM_DAYS * 86400000).toISOString());
    await supabaseAdmin.from("wearable_metrics").delete().eq("user_id", userId);

    // 3. Pick days that get a "hot flash cluster" + skin temp spike (last 14 days).
    const clusterDays = new Set<number>([1, 3, 6, 9, 12]); // daysAgo

    // 4. Seed daily_logs — mild improvement: earlier days worse than recent.
    const severitySymptoms: { name: string; base: number; trend: number }[] = [
      { name: "Night sweats",       base: 3, trend: -0.05 },
      { name: "Poor sleep",         base: 3, trend: -0.04 },
      { name: "Mood swings",        base: 3, trend: -0.06 },
      { name: "Brain fog",          base: 3, trend: -0.04 },
      { name: "Joint pain",         base: 2, trend: -0.02 },
      { name: "Anxiety",            base: 3, trend: -0.05 },
      { name: "Fatigue",            base: 3, trend: -0.04 },
      { name: "Headaches",          base: 2, trend: -0.03 },
    ];
    const logRows: any[] = [];
    for (let i = SYMPTOM_DAYS - 1; i >= 0; i--) {
      const lk = dayKey(i);
      // pseudo-random jitter deterministic per day
      const jitter = (seed: number) => ((Math.sin(i * 9.7 + seed) + 1) / 2 - 0.5); // -0.5..0.5
      for (const s of severitySymptoms) {
        const raw = s.base + s.trend * (SYMPTOM_DAYS - 1 - i) + jitter(s.name.length) * 1.2;
        const sev = Math.max(1, Math.min(4, Math.round(raw)));
        logRows.push({ user_id: userId, log_date: lk, symptom_name: s.name, severity: sev, count: 1 });
      }
    }
    if (logRows.length) {
      const { error } = await supabaseAdmin.from("daily_logs").insert(logRows);
      if (error) throw new Error("daily_logs insert: " + error.message);
    }

    // 5. Seed hot flash events — several per week, denser on cluster days.
    const eventRows: any[] = [];
    const hfCountByDay: Record<string, number> = {};
    for (let i = SYMPTOM_DAYS - 1; i >= 0; i--) {
      const lk = dayKey(i);
      // baseline: improving from ~6 events early to ~1 events recent
      const baseline = Math.max(0, Math.round(6 - (SYMPTOM_DAYS - 1 - i) * 0.25));
      const cluster = clusterDays.has(i) ? 8 : 0;
      const count = baseline + cluster;
      hfCountByDay[lk] = count;
      for (let k = 0; k < count; k++) {
        // spread events through the day (UTC) — evenings weighted
        const hour = 9 + Math.floor((k / Math.max(1, count)) * 14);
        const min = (k * 13) % 60;
        const ts = new Date(now.getTime() - i * 86400000);
        ts.setUTCHours(hour, min, 0, 0);
        eventRows.push({
          user_id: userId,
          symptom_name: "Hot flashes",
          occurred_at: ts.toISOString(),
          severity: clusterDays.has(i) ? 3 : 2,
        });
      }
      // Mirror to daily_logs.count for the Hot flashes cache row.
      if (count > 0) {
        logRows.push({ user_id: userId, log_date: lk, symptom_name: "Hot flashes", severity: null, count: Math.min(50, count) });
      }
    }
    if (eventRows.length) {
      const { error } = await supabaseAdmin.from("symptom_events").insert(eventRows);
      if (error) throw new Error("symptom_events insert: " + error.message);
    }
    // Upsert Hot flashes daily_logs cache rows.
    const hfCacheRows = Object.entries(hfCountByDay)
      .filter(([, c]) => c > 0)
      .map(([lk, c]) => ({ user_id: userId, log_date: lk, symptom_name: "Hot flashes", severity: null, count: Math.min(50, c as number) }));
    if (hfCacheRows.length) {
      const { error } = await supabaseAdmin.from("daily_logs").upsert(hfCacheRows, { onConflict: "user_id,log_date,symptom_name" });
      if (error) throw new Error("daily_logs (hot flash) upsert: " + error.message);
    }

    // 6. Seed wearable_metrics — 30 days, with skin-temp spikes on cluster days.
    //    Ensure a wearable_connection exists so the Today card / Symptom Intelligence Report
    //    consider the user "connected" for display purposes.
    await supabaseAdmin.from("wearable_connections").upsert(
      [{
        user_id: userId,
        provider: "fitbit",
        status: "connected",
        connected_at: new Date(now.getTime() - 21 * 86400000).toISOString(),
        last_synced_at: now.toISOString(),
      }],
      { onConflict: "user_id,provider" },
    );

    const wRows: any[] = [];
    for (let i = WEARABLE_DAYS - 1; i >= 0; i--) {
      const lk = dayKey(i);
      const jitter = (seed: number) => ((Math.sin(i * 7.3 + seed) + 1) / 2 - 0.5);
      // Sleep improves slightly over time
      const sleepMin = Math.round(360 + (WEARABLE_DAYS - 1 - i) * 1.5 + jitter(1) * 60);
      const sleepQ = Math.max(40, Math.min(95, Math.round(65 + (WEARABLE_DAYS - 1 - i) * 0.4 + jitter(2) * 15)));
      const rhr = Math.max(55, Math.min(80, Math.round(70 - (WEARABLE_DAYS - 1 - i) * 0.15 + jitter(3) * 4)));
      const hrv = Math.max(25, Math.min(75, Math.round(38 + (WEARABLE_DAYS - 1 - i) * 0.25 + jitter(4) * 8)));
      // Skin temp delta: small jitter, +0.6..+1.1°C spike on cluster days
      const baseTemp = jitter(5) * 0.3;
      const spike = clusterDays.has(i) ? 0.7 + Math.abs(jitter(6)) * 0.4 : 0;
      const skinTemp = Number((baseTemp + spike).toFixed(2));
      wRows.push({
        user_id: userId,
        provider: "fitbit",
        metric_date: lk,
        sleep_duration_minutes: sleepMin,
        sleep_quality_score: sleepQ,
        resting_heart_rate: rhr,
        hrv_ms: hrv,
        skin_temperature_c: skinTemp,
        steps: 4000 + Math.round(Math.abs(jitter(7)) * 6000),
        active_minutes: 15 + Math.round(Math.abs(jitter(8)) * 50),
        raw: { seeded: true },
      });
    }
    const { error: wErr } = await supabaseAdmin.from("wearable_metrics").upsert(wRows, { onConflict: "user_id,provider,metric_date" });
    if (wErr) throw new Error("wearable_metrics insert: " + wErr.message);

    // 7. Report counts.
    const { count: logCount } = await supabaseAdmin
      .from("daily_logs").select("id", { count: "exact", head: true }).eq("user_id", userId);
    const { count: evCount } = await supabaseAdmin
      .from("symptom_events").select("id", { count: "exact", head: true }).eq("user_id", userId).eq("symptom_name", "Hot flashes");
    const { count: wCount } = await supabaseAdmin
      .from("wearable_metrics").select("id", { count: "exact", head: true }).eq("user_id", userId);

    return {
      ok: true,
      symptomLogRows: logCount ?? 0,
      hotFlashEvents: evCount ?? 0,
      wearableDays: wCount ?? 0,
      clusterDates: Array.from(clusterDays).map(d => dayKey(d)),
    };
  });

// Sandbox-only: seed a complete 90-day patient covering every Symptom Intelligence Report
// section — backdated tracking, daily symptom + mood logs at >85% completion,
// irregular perimenopausal cycles, mid-window HRT initiation + adherence,
// realistic labs, hot-flash clusters, and 90 days of wearable metrics with
// skin-temp spikes overlapping the hot-flash days.
export const seedComplete90DayPatient = createServerFn({ method: "POST" })
  .middleware([requireAdminAuth])
  .handler(async ({ context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { userId } = context;
    const now = new Date();
    const TRACK_START_DAYS = 95;
    const SYMPTOM_DAYS = 90;
    const WEARABLE_DAYS = 90;
    const HRT_START_DAYS = 45;

    function dayKey(daysAgo: number): string {
      const d = new Date(now.getTime() - daysAgo * 86400000);
      const y = d.getUTCFullYear();
      const m = String(d.getUTCMonth() + 1).padStart(2, "0");
      const dd = String(d.getUTCDate()).padStart(2, "0");
      return `${y}-${m}-${dd}`;
    }
    const jitter = (i: number, seed: number) => ((Math.sin(i * 9.7 + seed) + 1) / 2 - 0.5); // -0.5..0.5

    // ---- 1. CLEAR prior seeded rows (full wipe for this user) ----
    await supabaseAdmin.from("daily_logs").delete().eq("user_id", userId);
    await supabaseAdmin.from("daily_moods").delete().eq("user_id", userId);
    await supabaseAdmin.from("daily_hrt").delete().eq("user_id", userId);
    await supabaseAdmin.from("symptom_events").delete().eq("user_id", userId);
    await supabaseAdmin.from("wearable_metrics").delete().eq("user_id", userId);
    await supabaseAdmin.from("periods").delete().eq("user_id", userId);
    await supabaseAdmin.from("lab_uploads").delete().eq("user_id", userId);

    // ---- 2. PROFILE: backdate, perimenopausal stage, DOB ~49y, HRT details ----
    const trackStart = new Date(now.getTime() - TRACK_START_DAYS * 86400000);
    const dobYear = now.getUTCFullYear() - 49;
    const dob = `${dobYear}-04-12`;
    const hrtStartDate = dayKey(HRT_START_DAYS);
    const lastPeriodDate = dayKey(2); // currently bleeding
    const healthProfile = {
      stage: "Perimenopause",
      dob,
      hrtType: "Estradiol patch + Progesterone",
      hrtTypes: ["Estradiol patch", "Progesterone"],
      hrtDose: "0.05 mg/24h patch + 100 mg micronized progesterone nightly",
      hrtSchedule: "twice-weekly",
      hrtStartDate,
      hrtDuration: "Started 45 days ago",
      otherMeds: "Vitamin D 2000 IU, Magnesium 400 mg",
      otherConditions: "None",
      device: "Fitbit",
    };
    // trial_started_at drives `user.signupDate` → milestoneDay in the report,
    // which gates the formal Symptom Direction section (Day 60+). Backdate
    // it alongside created_at so the seeded patient lands on Day 92.
    const trialStartedAt = new Date(now.getTime() - 92 * 86400000);
    const { error: profileErr } = await supabaseAdmin
      .from("profiles")
      .upsert({
        id: userId,
        created_at: trackStart.toISOString(),
        trial_started_at: trialStartedAt.toISOString(),
        trial_ends_at: new Date(now.getTime() + 365 * 86400000).toISOString(),
        is_test_user: true,
        onboarding_completed: true,
        name: "Sarah Lewis",
        life_stage: "Perimenopause",
        focus_area: "Symptom relief",
        last_period_date: lastPeriodDate,
        hrt_start_date: hrtStartDate,
        stage_changed_at: trackStart.toISOString(),
        wearable_device: "fitbit",
        health_profile: healthProfile,
      }, { onConflict: "id" });
    if (profileErr) throw new Error("profiles upsert: " + profileErr.message);

    // ---- 3. DAILY SYMPTOM LOGS — 85%+ completion, improving after HRT start ----
    // Pre-HRT (days 90..46): worse; Post-HRT (days 45..0): improving.
    const symptoms = [
      { name: "Night sweats", baseHigh: 3.2, baseLow: 1.6 },
      { name: "Poor sleep",   baseHigh: 3.0, baseLow: 1.5 },
      { name: "Mood swings",  baseHigh: 2.9, baseLow: 1.4 },
      { name: "Brain fog",    baseHigh: 2.8, baseLow: 1.5 },
      { name: "Joint pain",   baseHigh: 2.4, baseLow: 1.7 },
      { name: "Anxiety",      baseHigh: 2.9, baseLow: 1.4 },
      { name: "Fatigue",      baseHigh: 3.4, baseLow: 2.7 },
      { name: "Headaches",    baseHigh: 2.2, baseLow: 1.2 },
    ];
    // Cluster days for hot flashes (heavier pre-HRT, sparser post-HRT).
    const clusterDays = new Set<number>([
      85, 80, 77, 72, 68, 63, 58, 52, 48, // pre-HRT cluster
      40, 33, 25, 18, 10,                  // post-HRT, fewer
    ]);
    // Skip ~12 days out of 90 to land ~87% completion.
    const skipDays = new Set<number>([88, 81, 74, 64, 55, 47, 38, 30, 22, 15, 8, 3]);

    const logRows: any[] = [];
    const moodRows: any[] = [];
    for (let i = SYMPTOM_DAYS - 1; i >= 0; i--) {
      const lk = dayKey(i);
      const skip = skipDays.has(i);
      // post-HRT progress (0 pre, ramps to 1 over 45 days post-start)
      const postProgress = i <= HRT_START_DAYS ? (HRT_START_DAYS - i) / HRT_START_DAYS : 0;
      // Mood improves over time (0 = great, 4 = terrible per validate_daily_mood range).
      if (!skip) {
        const moodRaw = (i > HRT_START_DAYS ? 2.6 : 2.6 - postProgress * 1.6) + jitter(i, 11) * 0.8;
        const mood = Math.max(0, Math.min(4, Math.round(moodRaw)));
        moodRows.push({ user_id: userId, log_date: lk, mood });
      }
      if (skip) continue;
      for (const s of symptoms) {
        const target = s.baseHigh - (s.baseHigh - s.baseLow) * postProgress;
        const raw = target + jitter(i, s.name.length) * 1.0;
        const sev = Math.max(1, Math.min(4, Math.round(raw)));
        logRows.push({ user_id: userId, log_date: lk, symptom_name: s.name, severity: sev, count: 1 });
      }
    }

    // ---- 4. HOT FLASH symptom_events + daily_logs cache row ----
    const eventRows: any[] = [];
    const hfCountByDay: Record<string, number> = {};
    for (let i = SYMPTOM_DAYS - 1; i >= 0; i--) {
      const lk = dayKey(i);
      const postProgress = i <= HRT_START_DAYS ? (HRT_START_DAYS - i) / HRT_START_DAYS : 0;
      const baseline = i > HRT_START_DAYS
        ? 2 + Math.round(Math.abs(jitter(i, 21)) * 2) // 2..4 events/day pre-HRT
        : Math.max(0, Math.round(2 - postProgress * 2 + jitter(i, 22) * 1.2));
      const cluster = clusterDays.has(i) ? (i > HRT_START_DAYS ? 6 : 4) : 0;
      const count = baseline + cluster;
      if (count === 0) continue;
      hfCountByDay[lk] = count;
      for (let k = 0; k < count; k++) {
        const hour = 8 + Math.floor((k / Math.max(1, count)) * 14);
        const min = (k * 13) % 60;
        const ts = new Date(now.getTime() - i * 86400000);
        ts.setUTCHours(hour, min, 0, 0);
        eventRows.push({
          user_id: userId,
          symptom_name: "Hot flashes",
          occurred_at: ts.toISOString(),
          severity: clusterDays.has(i) ? 3 : 2,
        });
      }
      logRows.push({ user_id: userId, log_date: lk, symptom_name: "Hot flashes", severity: null, count: Math.min(50, count) });
    }

    if (logRows.length) {
      const { error } = await supabaseAdmin.from("daily_logs").upsert(logRows, { onConflict: "user_id,log_date,symptom_name" });
      if (error) throw new Error("daily_logs upsert: " + error.message);
    }
    if (moodRows.length) {
      const { error } = await supabaseAdmin.from("daily_moods").upsert(moodRows, { onConflict: "user_id,log_date" });
      if (error) throw new Error("daily_moods upsert: " + error.message);
    }
    if (eventRows.length) {
      const { error } = await supabaseAdmin.from("symptom_events").insert(eventRows);
      if (error) throw new Error("symptom_events insert: " + error.message);
    }

    // ---- 5. PERIODS — irregular perimenopausal, 4 cycles in 90 days ----
    // Lengths: 5, 4, 7, current(3 so far). Gaps: 28, 27, 34 days → irregular.
    const periodPlan = [
      { startDaysAgo: 87, endDaysAgo: 83, flows: ["Light", "Medium", "Medium", "Heavy", "Light"] },
      { startDaysAgo: 61, endDaysAgo: 58, flows: ["Spotting", "Light", "Light", "Spotting"] },
      { startDaysAgo: 33, endDaysAgo: 27, flows: ["Light", "Medium", "Heavy", "Heavy", "Medium", "Light", "Spotting"] },
      { startDaysAgo: 2,  endDaysAgo: null, flows: ["Medium", "Heavy"] },
    ];
    const periodRows = periodPlan.map(p => {
      const startKey = dayKey(p.startDaysAgo);
      const endKey = p.endDaysAgo != null ? dayKey(p.endDaysAgo) : null;
      const flow_log = p.flows.map((level, idx) => {
        const dDate = new Date(now.getTime() - (p.startDaysAgo - idx) * 86400000);
        const y = dDate.getUTCFullYear();
        const m = String(dDate.getUTCMonth() + 1).padStart(2, "0");
        const dd = String(dDate.getUTCDate()).padStart(2, "0");
        return { date: `${y}-${m}-${dd}`, level };
      });
      return {
        user_id: userId,
        start_date: startKey,
        end_date: endKey,
        flow_log,
        notes: null,
      };
    });
    const { error: pErr } = await supabaseAdmin.from("periods").insert(periodRows);
    if (pErr) throw new Error("periods insert: " + pErr.message);

    // ---- 6. DAILY HRT adherence — from HRT start to today, ~92% adherence ----
    const hrtRows: any[] = [];
    for (let i = HRT_START_DAYS; i >= 0; i--) {
      const lk = dayKey(i);
      // skip a few days (no log) and miss a few doses
      if ([42, 35, 21].includes(i)) continue; // unlogged
      const missed = [40, 28, 14, 6].includes(i);
      hrtRows.push({ user_id: userId, log_date: lk, hrt_taken: !missed });
    }
    if (hrtRows.length) {
      const { error } = await supabaseAdmin.from("daily_hrt").upsert(hrtRows, { onConflict: "user_id,log_date" });
      if (error) throw new Error("daily_hrt upsert: " + error.message);
    }

    // ---- 7. LAB UPLOADS — two panels, realistic perimenopausal values ----
    const labRows = [
      {
        user_id: userId,
        lab_date: dayKey(70),
        lab_name: "Quest Diagnostics — Hormone panel",
        estradiol_value: 38, estradiol_unit: "pg/mL",
        fsh_value: 28.4, fsh_unit: "mIU/mL",
        lh_value: 18.2, lh_unit: "mIU/mL",
        progesterone_value: 0.6, progesterone_unit: "ng/mL",
        tsh_value: 2.1, tsh_unit: "µIU/mL",
      },
      {
        user_id: userId,
        lab_date: dayKey(14),
        lab_name: "LabCorp — Follow-up panel",
        estradiol_value: 62, estradiol_unit: "pg/mL", // on patch — higher
        fsh_value: 22.1, fsh_unit: "mIU/mL",
        lh_value: 14.8, lh_unit: "mIU/mL",
        progesterone_value: 1.4, progesterone_unit: "ng/mL",
        tsh_value: 1.9, tsh_unit: "µIU/mL",
      },
    ];
    const { error: lErr } = await supabaseAdmin.from("lab_uploads").insert(labRows);
    if (lErr) throw new Error("lab_uploads insert: " + lErr.message);

    // ---- 8. WEARABLE CONNECTION + 90 days of wearable_metrics ----
    await supabaseAdmin.from("wearable_connections").upsert(
      [{
        user_id: userId,
        provider: "fitbit",
        status: "connected",
        connected_at: trackStart.toISOString(),
        last_synced_at: now.toISOString(),
      }],
      { onConflict: "user_id,provider" },
    );

    const wRows: any[] = [];
    for (let i = WEARABLE_DAYS - 1; i >= 0; i--) {
      const lk = dayKey(i);
      const postProgress = i <= HRT_START_DAYS ? (HRT_START_DAYS - i) / HRT_START_DAYS : 0;
      // Pre-HRT: poorer sleep, higher RHR, lower HRV. Post-HRT: improving.
      const sleepMin = Math.round(340 + postProgress * 70 + jitter(i, 1) * 45);
      const sleepQ = Math.max(40, Math.min(95, Math.round(58 + postProgress * 22 + jitter(i, 2) * 12)));
      const rhr = Math.max(54, Math.min(82, Math.round(73 - postProgress * 8 + jitter(i, 3) * 4)));
      const hrv = Math.max(22, Math.min(78, Math.round(34 + postProgress * 16 + jitter(i, 4) * 8)));
      // Skin temp delta around 0, with +0.7..+1.1°C spike on cluster days.
      const baseTemp = jitter(i, 5) * 0.25;
      const spike = clusterDays.has(i) ? 0.7 + Math.abs(jitter(i, 6)) * 0.4 : 0;
      const skinTemp = Number((baseTemp + spike).toFixed(2));
      const steps = 3800 + Math.round(Math.abs(jitter(i, 7)) * 7000);
      const activeMinutes = 12 + Math.round(Math.abs(jitter(i, 8)) * 55);
      wRows.push({
        user_id: userId,
        provider: "fitbit",
        metric_date: lk,
        sleep_duration_minutes: sleepMin,
        sleep_quality_score: sleepQ,
        resting_heart_rate: rhr,
        hrv_ms: hrv,
        skin_temperature_c: skinTemp,
        steps,
        active_minutes: activeMinutes,
        raw: { seeded: true },
      });
    }
    const { error: wErr } = await supabaseAdmin.from("wearable_metrics").upsert(wRows, { onConflict: "user_id,provider,metric_date" });
    if (wErr) throw new Error("wearable_metrics insert: " + wErr.message);

    // ---- 9. REPORT counts ----
    const counts = await Promise.all([
      supabaseAdmin.from("daily_logs").select("id", { count: "exact", head: true }).eq("user_id", userId),
      supabaseAdmin.from("daily_moods").select("id", { count: "exact", head: true }).eq("user_id", userId),
      supabaseAdmin.from("daily_hrt").select("id", { count: "exact", head: true }).eq("user_id", userId),
      supabaseAdmin.from("symptom_events").select("id", { count: "exact", head: true }).eq("user_id", userId).eq("symptom_name", "Hot flashes"),
      supabaseAdmin.from("wearable_metrics").select("id", { count: "exact", head: true }).eq("user_id", userId),
      supabaseAdmin.from("periods").select("id", { count: "exact", head: true }).eq("user_id", userId),
      supabaseAdmin.from("lab_uploads").select("id", { count: "exact", head: true }).eq("user_id", userId),
    ]);

    return {
      ok: true,
      symptomLogRows: counts[0].count ?? 0,
      moodRows: counts[1].count ?? 0,
      hrtAdherenceRows: counts[2].count ?? 0,
      hotFlashEvents: counts[3].count ?? 0,
      wearableDays: counts[4].count ?? 0,
      periodRows: counts[5].count ?? 0,
      labPanels: counts[6].count ?? 0,
      hrtStartDate,
      lastPeriodDate,
      dob,
    };
  });

// Sandbox-only: seed ~90 days of wearable_metrics + daily_logs with a real
// but noisy coupling between overnight recovery and that day's symptoms.
// Only operates on the currently logged-in account when is_test_user=true.
// Uses the SAME adverse thresholds the forecast uses (sleep ≥45 min below
// the prior-14-night median, HRV ≥10% below, RHR ≥3 bpm above) so the
// existing computeSymptomRiskForecast bands these as Elevated / Moderate /
// Lower without any reimplementation.
export const seedCoupledValidationData = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    if (!isJunctionSandbox()) {
      throw new Error("Seeding is only available in the sandbox environment.");
    }
    const { userId } = context;

    // Hard guard: never touch a non-test account.
    const { data: prof, error: pErr } = await supabaseAdmin
      .from("profiles")
      .select("is_test_user")
      .eq("id", userId)
      .maybeSingle();
    if (pErr) throw new Error("profiles read: " + pErr.message);
    if (!prof?.is_test_user) {
      throw new Error("Refusing to seed: account is not marked is_test_user.");
    }

    const now = new Date();
    const TOTAL = 90;
    const CLEAR_WINDOW = 95;
    const BASELINE_DAYS = 14;

    function dayKey(daysAgo: number): string {
      const d = new Date(now.getTime() - daysAgo * 86400000);
      const y = d.getUTCFullYear();
      const m = String(d.getUTCMonth() + 1).padStart(2, "0");
      const dd = String(d.getUTCDate()).padStart(2, "0");
      return `${y}-${m}-${dd}`;
    }
    const clearFrom = dayKey(CLEAR_WINDOW - 1);
    const clearTo = dayKey(0);

    // 1. Clear prior rows in the window for this user (idempotent re-run).
    {
      const { error } = await supabaseAdmin
        .from("wearable_metrics").delete()
        .eq("user_id", userId)
        .gte("metric_date", clearFrom).lte("metric_date", clearTo);
      if (error) throw new Error("wearable_metrics clear: " + error.message);
    }
    {
      const { error } = await supabaseAdmin
        .from("daily_logs").delete()
        .eq("user_id", userId)
        .gte("log_date", clearFrom).lte("log_date", clearTo);
      if (error) throw new Error("daily_logs clear: " + error.message);
    }

    // 2. Day plan: first BASELINE_DAYS calm to establish a stable baseline,
    //    then shuffle 10 flare + 22 partial + remaining calm across the rest.
    type Plan = "flare" | "partial" | "calm";
    const candidateDays = TOTAL - BASELINE_DAYS;
    const FLARE = 10;
    const PARTIAL = 22;
    const CALM_CAND = candidateDays - FLARE - PARTIAL;
    const candPlan: Plan[] = [
      ...Array<Plan>(FLARE).fill("flare"),
      ...Array<Plan>(PARTIAL).fill("partial"),
      ...Array<Plan>(CALM_CAND).fill("calm"),
    ];
    for (let i = candPlan.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [candPlan[i], candPlan[j]] = [candPlan[j], candPlan[i]];
    }
    const plans: Plan[] = [
      ...Array<Plan>(BASELINE_DAYS).fill("calm"),
      ...candPlan,
    ];

    function gauss(): number {
      let u = 0, v = 0;
      while (u === 0) u = Math.random();
      while (v === 0) v = Math.random();
      return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
    }
    function median(arr: number[]): number {
      const s = [...arr].sort((a, b) => a - b);
      const n = s.length;
      const mid = Math.floor(n / 2);
      return n % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
    }

    const SLEEP_ANCHOR = 420;
    const HRV_ANCHOR = 45;
    const RHR_ANCHOR = 62;

    const sleepHist: number[] = [];
    const hrvHist: number[] = [];
    const rhrHist: number[] = [];

    await supabaseAdmin.from("wearable_connections").upsert(
      [{
        user_id: userId, provider: "fitbit", status: "connected",
        connected_at: new Date(now.getTime() - TOTAL * 86400000).toISOString(),
        last_synced_at: now.toISOString(),
      }],
      { onConflict: "user_id,provider" },
    );

    const wRows: any[] = [];
    const logRows: any[] = [];
    const symptoms = [
      "Night sweats", "Poor sleep", "Mood swings",
      "Brain fog", "Anxiety", "Fatigue",
    ];

    for (let dayIdx = 0; dayIdx < TOTAL; dayIdx++) {
      const daysAgo = TOTAL - 1 - dayIdx;
      const lk = dayKey(daysAgo);
      const plan = plans[dayIdx];

      const baseSleep = sleepHist.length >= 7
        ? median(sleepHist.slice(-BASELINE_DAYS)) : SLEEP_ANCHOR;
      const baseHrv = hrvHist.length >= 7
        ? median(hrvHist.slice(-BASELINE_DAYS)) : HRV_ANCHOR;
      const baseRhr = rhrHist.length >= 7
        ? median(rhrHist.slice(-BASELINE_DAYS)) : RHR_ANCHOR;

      let adv = { sleep: false, hrv: false, rhr: false };
      if (plan === "flare") {
        adv = { sleep: true, hrv: true, rhr: true };
      } else if (plan === "partial") {
        const pick = Math.floor(Math.random() * 3);
        if (pick === 0) adv = { sleep: true, hrv: true, rhr: false };
        else if (pick === 1) adv = { sleep: true, hrv: false, rhr: true };
        else adv = { sleep: false, hrv: true, rhr: true };
      }

      const sleep = Math.round(
        adv.sleep ? baseSleep - 60 + gauss() * 6 : baseSleep + gauss() * 12,
      );
      const hrv = Number((
        adv.hrv ? baseHrv * 0.83 + gauss() * 1.2 : baseHrv + gauss() * 2
      ).toFixed(1));
      const rhr = Math.round(
        adv.rhr ? baseRhr + 5 + gauss() * 0.8 : baseRhr + gauss() * 1.2,
      );
      const sleepQ = Math.max(40, Math.min(95, Math.round(72 + gauss() * 6)));
      const skinTemp = Number((36.6 + gauss() * 0.1).toFixed(2));

      sleepHist.push(sleep);
      hrvHist.push(hrv);
      rhrHist.push(rhr);

      wRows.push({
        user_id: userId, provider: "fitbit", metric_date: lk,
        sleep_duration_minutes: sleep,
        sleep_quality_score: sleepQ,
        hrv_ms: hrv,
        resting_heart_rate: rhr,
        skin_temperature_c: skinTemp,
        steps: 4000 + Math.round(Math.abs(gauss()) * 4000),
        active_minutes: 15 + Math.round(Math.abs(gauss()) * 40),
        raw: { seeded: "coupled" },
      });

      const adverseCount =
        (adv.sleep ? 1 : 0) + (adv.hrv ? 1 : 0) + (adv.rhr ? 1 : 0);
      const target = Math.max(
        1, Math.min(4, 2.0 + 0.5 * adverseCount + gauss() * 0.45),
      );
      for (const s of symptoms) {
        const raw = target + gauss() * 0.6;
        const sev = Math.max(1, Math.min(4, Math.round(raw)));
        logRows.push({
          user_id: userId, log_date: lk,
          symptom_name: s, severity: sev, count: 1,
        });
      }
    }

    const chunk = <T,>(a: T[], n: number): T[][] => {
      const out: T[][] = [];
      for (let i = 0; i < a.length; i += n) out.push(a.slice(i, i + n));
      return out;
    };
    for (const c of chunk(wRows, 200)) {
      const { error } = await supabaseAdmin.from("wearable_metrics").upsert(c, { onConflict: "user_id,provider,metric_date" });
      if (error) throw new Error("wearable_metrics insert: " + error.message);
    }
    for (const c of chunk(logRows, 500)) {
      const { error } = await supabaseAdmin.from("daily_logs").insert(c);
      if (error) throw new Error("daily_logs insert: " + error.message);
    }

    const flareCount = plans.filter((p) => p === "flare").length;
    const partialCount = plans.filter((p) => p === "partial").length;
    return {
      ok: true,
      totalDays: TOTAL,
      flareDays: flareCount,
      partialDays: partialCount,
      calmDays: TOTAL - flareCount - partialCount,
      wearableRows: wRows.length,
      symptomRows: logRows.length,
    };
  });


